import { createContext, useContext, useReducer, useCallback, useEffect, ReactNode } from 'react';

// ─── Item Types ────────────────────────────────────────────────

export interface ItemState {
  id: string;
  isObtained: boolean;
  state: number;       // upgrade level (0, 1, 2, ...)
  isChecked: boolean;  // WoTH marker
}

export interface OotItemDto {
  name: string;
  tags: string[];
  img: string;
  childImage?: string;
}

export interface GoalDto {
  name: string;
  tags: string[];
  img: string;
}

export type SometimesKind = 'regular' | 'dual' | 'entrance';

export interface HintLocationDto {
  location: { id: number; name: string; areaName: string } | null;
  items: OotItemDto[];
  goal: GoalDto | null;
  count: number;
  player: string | null;
  /** Free text or selected check name (used by Sometimes hints). */
  text?: string;
  /** Reference to a CheckDto when picked from the list; null/undefined for free text. */
  checkId?: number | null;
  /** Sub-category for Sometimes hints. Defaults to 'regular' when undefined. */
  kind?: SometimesKind;
}

export interface ShopItemDto {
  item: OotItemDto | null;
  price: string;
}

export interface LocationDto {
  id: number;
  name: string;
  areaId: number;
  areaName: string;
  tags: string[];
}

// ─── Tracker State ─────────────────────────────────────────────

export interface TrackerState {
  // Items
  items: ItemState[];

  // Hint counts
  nbAlways: number;
  nbSometimes: number;
  nbGoals: number;
  nbBarren: number;

  // Hint data
  barren: LocationDto[];
  goals: Record<number, HintLocationDto>;
  sometimesHints: Record<number, HintLocationDto>;
  /** Active Always hints (ordered list of hint names). User-managed. */
  alwaysHintNames: string[];
  /** Item tracked next to each Always hint, keyed by hint name. */
  alwaysItems: Record<string, OotItemDto | null>;

  // Settings toggles
  isMultiworld: boolean;
  isDungeonShuffle: boolean;
  isBossShuffle: boolean;
  isSpawnRandom: boolean;
  isWarpSongRandom: boolean;
  isOwlRandom: boolean;
  isMixedPoolGrotto: boolean;
  isMixedPoolInterior: boolean;
  isMixedPoolDungeon: boolean;
  isMixedPoolOverworld: boolean;
  isWoth: boolean;
  isScrubsanity: boolean;
  isCowsanity: boolean;
  useShopsanity: boolean;
  useItemTracker: boolean;
  useItemTrackerReduce: boolean;
  isKeysanity: boolean;
  isKeyring: boolean;
  isEnnemySoul: boolean;
  useTrials: boolean;
  nbMultiworldPlayer: number;
  players: string[];

  // Shop tracking
  shops: Record<string, ShopItemDto[]>;

  // Dungeon/Boss assignments
  dungeonAssignments: Record<string, string | null>;
  bossAssignments: Record<string, string | null>;

  // Warp destinations
  warpDestinations: Record<string, LocationDto | null>;

  // Keysanity
  smallKeys: Record<string, number>;
  bossKeys: Record<string, boolean>;
  dungeonMq: Record<string, boolean>;
}

// ─── Actions ───────────────────────────────────────────────────

type TrackerAction =
  | { type: 'SET_ITEMS'; items: ItemState[] }
  | { type: 'UPDATE_ITEM'; id: string; updates: Partial<ItemState> }
  | { type: 'SET_SETTING'; key: keyof TrackerState; value: unknown }
  | { type: 'SET_NB_GOALS'; value: number }
  | { type: 'SET_NB_ALWAYS'; value: number }
  | { type: 'SET_NB_SOMETIMES'; value: number }
  | { type: 'SET_NB_BARREN'; value: number }
  | { type: 'SET_BARREN'; barren: LocationDto[] }
  | { type: 'SET_GOAL'; id: number; goal: HintLocationDto }
  | { type: 'SET_SOMETIMES'; id: number; hint: HintLocationDto }
  | { type: 'SET_ALWAYS_ITEM'; hintName: string; item: OotItemDto | null }
  | { type: 'ADD_ALWAYS'; hintName: string }
  | { type: 'REMOVE_ALWAYS'; hintName: string }
  | {
      type: 'APPLY_LAYOUT';
      layout: {
        nbGoals: number;
        nbSometimes: number;
        nbBarren: number;
        isWoth: boolean;
        alwaysHintNames: string[];
        sometimesKinds: SometimesKind[];
        startingItems?: Array<{ id: string; state?: number }>;
      };
    }
  | { type: 'SET_SMALL_KEY'; dungeon: string; count: number }
  | { type: 'SET_BOSS_KEY'; dungeon: string; obtained: boolean }
  | { type: 'SET_DUNGEON_MQ'; dungeon: string; isMq: boolean }
  | { type: 'SET_SHOP_ITEM'; shop: string; index: number; item: ShopItemDto }
  | { type: 'SET_WARP'; warp: string; destination: LocationDto | null }
  | { type: 'SET_DUNGEON_ASSIGNMENT'; dungeon: string; assignment: string | null }
  | { type: 'SET_BOSS_ASSIGNMENT'; dungeon: string; boss: string | null }
  | { type: 'RESET' };

// ─── Initial State ─────────────────────────────────────────────

const initialState: TrackerState = {
  items: [],
  nbAlways: 0,
  nbSometimes: 2,
  nbGoals: 4,
  nbBarren: 3,
  barren: [],
  goals: {},
  sometimesHints: {},
  alwaysHintNames: [],
  alwaysItems: {},
  isMultiworld: false,
  isDungeonShuffle: false,
  isBossShuffle: false,
  isSpawnRandom: false,
  isWarpSongRandom: false,
  isOwlRandom: false,
  isMixedPoolGrotto: false,
  isMixedPoolInterior: false,
  isMixedPoolDungeon: false,
  isMixedPoolOverworld: false,
  isWoth: false,
  isScrubsanity: false,
  isCowsanity: false,
  useShopsanity: false,
  useItemTracker: true,
  useItemTrackerReduce: false,
  isKeysanity: false,
  isKeyring: false,
  isEnnemySoul: false,
  useTrials: false,
  nbMultiworldPlayer: 2,
  players: ['Player 1', 'Player 2'],
  shops: {},
  dungeonAssignments: {},
  bossAssignments: {},
  warpDestinations: {},
  smallKeys: {},
  bossKeys: {},
  dungeonMq: {},
};

// ─── Reducer ───────────────────────────────────────────────────

function trackerReducer(state: TrackerState, action: TrackerAction): TrackerState {
  switch (action.type) {
    case 'SET_ITEMS':
      return { ...state, items: action.items };

    case 'UPDATE_ITEM':
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.id ? { ...item, ...action.updates } : item
        ),
      };

    case 'SET_SETTING':
      return { ...state, [action.key]: action.value };

    case 'SET_NB_GOALS':
      return { ...state, nbGoals: action.value };

    case 'SET_NB_ALWAYS':
      return { ...state, nbAlways: action.value };

    case 'SET_NB_SOMETIMES':
      return { ...state, nbSometimes: action.value };

    case 'SET_NB_BARREN':
      return { ...state, nbBarren: action.value };

    case 'SET_BARREN':
      return { ...state, barren: action.barren };

    case 'SET_GOAL':
      return { ...state, goals: { ...state.goals, [action.id]: action.goal } };

    case 'SET_SOMETIMES':
      return { ...state, sometimesHints: { ...state.sometimesHints, [action.id]: action.hint } };

    case 'SET_ALWAYS_ITEM':
      return { ...state, alwaysItems: { ...state.alwaysItems, [action.hintName]: action.item } };

    case 'ADD_ALWAYS':
      if (state.alwaysHintNames.includes(action.hintName)) return state;
      return { ...state, alwaysHintNames: [...state.alwaysHintNames, action.hintName] };

    case 'REMOVE_ALWAYS': {
      // Clear primary slot AND the dual secondary slot under `${name}__2`.
      const remainingItems = { ...state.alwaysItems };
      delete remainingItems[action.hintName];
      delete remainingItems[`${action.hintName}__2`];
      return {
        ...state,
        alwaysHintNames: state.alwaysHintNames.filter((n) => n !== action.hintName),
        alwaysItems: remainingItems,
      };
    }

    case 'APPLY_LAYOUT': {
      const sometimesHints: Record<number, HintLocationDto> = {};
      for (let i = 0; i < action.layout.nbSometimes; i++) {
        sometimesHints[i] = {
          location: null,
          items: [],
          goal: null,
          count: 0,
          player: null,
          text: '',
          checkId: null,
          kind: action.layout.sometimesKinds[i] ?? 'regular',
        };
      }
      // Reset all items to unobtained, then apply starting inventory from the layout.
      const itemsById = new Map(state.items.map((i) => [i.id, i]));
      const resetItems: ItemState[] = state.items.map((i) => ({
        ...i,
        isObtained: false,
        state: 0,
        isChecked: false,
      }));
      const resetById = new Map(resetItems.map((i) => [i.id, i]));
      for (const starting of action.layout.startingItems ?? []) {
        const upgradeState = starting.state ?? 0;
        const existing = resetById.get(starting.id);
        const next: ItemState = {
          id: starting.id,
          isObtained: true,
          state: upgradeState,
          isChecked: existing?.isChecked ?? false,
        };
        resetById.set(starting.id, next);
        // Preserve original ItemState shape for items not yet registered.
        if (!itemsById.has(starting.id)) {
          resetItems.push(next);
        } else {
          const idx = resetItems.findIndex((i) => i.id === starting.id);
          if (idx >= 0) resetItems[idx] = next;
        }
      }
      return {
        ...state,
        items: resetItems,
        nbGoals: action.layout.nbGoals,
        nbSometimes: action.layout.nbSometimes,
        nbBarren: action.layout.nbBarren,
        isWoth: action.layout.isWoth,
        alwaysHintNames: action.layout.alwaysHintNames,
        // Wipe filled-in hint data: a layout import is a fresh start.
        goals: {},
        sometimesHints,
        barren: [],
        alwaysItems: {},
      };
    }

    case 'SET_SMALL_KEY':
      return { ...state, smallKeys: { ...state.smallKeys, [action.dungeon]: action.count } };

    case 'SET_BOSS_KEY':
      return { ...state, bossKeys: { ...state.bossKeys, [action.dungeon]: action.obtained } };

    case 'SET_DUNGEON_MQ':
      return { ...state, dungeonMq: { ...state.dungeonMq, [action.dungeon]: action.isMq } };

    case 'SET_SHOP_ITEM': {
      const shopItems = [...(state.shops[action.shop] ?? [])];
      shopItems[action.index] = action.item;
      return { ...state, shops: { ...state.shops, [action.shop]: shopItems } };
    }

    case 'SET_WARP':
      return { ...state, warpDestinations: { ...state.warpDestinations, [action.warp]: action.destination } };

    case 'SET_DUNGEON_ASSIGNMENT':
      return { ...state, dungeonAssignments: { ...state.dungeonAssignments, [action.dungeon]: action.assignment } };

    case 'SET_BOSS_ASSIGNMENT':
      return { ...state, bossAssignments: { ...state.bossAssignments, [action.dungeon]: action.boss } };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
}

// ─── Persistence ───────────────────────────────────────────────

const STORAGE_KEY = 'tracker-state';

function loadState(): TrackerState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return initialState;
    return { ...initialState, ...JSON.parse(stored) };
  } catch {
    return initialState;
  }
}

// ─── Context ───────────────────────────────────────────────────

interface TrackerContextType {
  state: TrackerState;
  dispatch: React.Dispatch<TrackerAction>;
  // Convenience methods
  updateItem: (id: string, updates: Partial<ItemState>) => void;
  toggleSetting: (key: keyof TrackerState) => void;
  setSetting: <K extends keyof TrackerState>(key: K, value: TrackerState[K]) => void;
  reset: () => void;
}

const TrackerContext = createContext<TrackerContextType | null>(null);

export function TrackerProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(trackerReducer, undefined, loadState);

  // Persist state changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const updateItem = useCallback((id: string, updates: Partial<ItemState>) => {
    dispatch({ type: 'UPDATE_ITEM', id, updates });
  }, []);

  const toggleSetting = useCallback((key: keyof TrackerState) => {
    dispatch({ type: 'SET_SETTING', key, value: !(state[key]) });
  }, [state]);

  const setSetting = useCallback(<K extends keyof TrackerState>(key: K, value: TrackerState[K]) => {
    dispatch({ type: 'SET_SETTING', key, value });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return (
    <TrackerContext.Provider value={{ state, dispatch, updateItem, toggleSetting, setSetting, reset }}>
      {children}
    </TrackerContext.Provider>
  );
}

export function useTracker() {
  const context = useContext(TrackerContext);
  if (!context) throw new Error('useTracker must be used within TrackerProvider');
  return context;
}

// Re-export types for convenience
export type { TrackerAction };
